import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Dropdown } from '@/components'
import { TeamRole } from '@/consts/team'
import { formatPrice } from '@/utils/formatter'

import { AddMemberForm, TEAM_ROLE_OPTIONS } from '../types/types'
import { randomPassword } from '../utils/utils'

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="t-sb text-primary flex items-center gap-1">
      {children} {required && <span className="text-error">*</span>}
    </label>
  )
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-11 px-4 rounded-lg border border-neutral-200 outline-none focus:border-primary t-sm"
    />
  )
}

function TextArea({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <textarea
      rows={5}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full p-4 rounded-lg border border-neutral-200 outline-none focus:border-primary t-sm"
    />
  )
}

function randomUsername() {
  const nouns = ['katie', 'mike', 'linda', 'james', 'sara', 'alex', 'emily', 'tony']
  const n = nouns[Math.floor(Math.random() * nouns.length)]
  const num = Math.floor(Math.random() * 90) + 10
  return `${n}${num}`
}

function sanitizeTo2dp(input: string) {
  let x = input.replace(/[^\d.]/g, '')

  const firstDot = x.indexOf('.')
  if (firstDot !== -1) {
    x = x.slice(0, firstDot + 1) + x.slice(firstDot + 1).replace(/\./g, '')
  }

  const [int = '', frac = ''] = x.split('.')
  const frac3 = frac.slice(0, 2)

  if (firstDot === -1) return int
  if (int === '' && frac3 === '') return ''
  return `${int || '0'}.${frac3}`
}

export type AddMemberFormViewHandle = {
  isValid: () => boolean
  getPayload: () => AddMemberForm | null
  triggerSubmit: () => void
}

export const AddMemberFormView = forwardRef<
  AddMemberFormViewHandle,
  {
    balance: number
    defaultRole?: TeamRole
    onCancel: () => void
    onSubmit: (payload: AddMemberForm) => void | Promise<void>
    teamDomain: string
    loading: boolean
    onValidityChange?: (valid: boolean) => void
  }
>(function AddMemberFormView(
  { balance, defaultRole = TeamRole.Normal, onSubmit, teamDomain, loading, onValidityChange },
  ref,
) {
  const { t } = useTranslation()

  // Generate translated role options
  const roleOptions = TEAM_ROLE_OPTIONS.map((option) => ({
    value: option.value,
    label: t(`team.role.${option.value === TeamRole.Admin ? 'admin' : 'member'}`),
  }))

  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<TeamRole>(defaultRole ?? TeamRole.Normal)
  const [credit, setCredit] = useState('')
  const [alias, setAlias] = useState('')
  const [note, setNote] = useState('')
  const [domainWarn, setDomainWarn] = useState<string>('')

  const USERNAME_RE = /^[A-Za-z0-9_-]{1,16}$/
  const PASSWORD_RE = /^[^\s]{8,20}$/

  const PASS_MIN = 8
  const PASS_MAX = 20
  const hasSpace = /\s/.test(password)
  const tooShort = password.length > 0 && password.length < PASS_MIN
  const tooLong = password.length > PASS_MAX
  const isPasswordValid = PASSWORD_RE.test(password) && !hasSpace
  const accountUserName = ''

  const balanceText = formatPrice(balance, false, '')

  function sanitizeAccountInput(v: string) {
    const trimmed = v.trim()
    if (!trimmed.includes('@')) {
      setDomainWarn('')
      setAccount(trimmed)
      return
    }
    const [local, dom] = trimmed.split('@', 2)
    setAccount(local)
    if (dom && teamDomain && dom !== teamDomain) {
      setDomainWarn(t('team.member.form.domainWarning', { detected: dom, team: teamDomain }))
    } else {
      setDomainWarn('')
    }
  }

  const isUsernameValid = USERNAME_RE.test(account)
  const enoughBalance = Number(credit) >= 0 && Number(credit) <= Number(balanceText)

  const canSubmit =
    Boolean(account.trim()) &&
    Boolean(password.trim()) &&
    Boolean(role) &&
    Boolean(credit.trim()) &&
    isPasswordValid &&
    enoughBalance &&
    isUsernameValid

  // 告知父層目前是否可提交（控制外部 Footer 按鈕）
  useEffect(() => {
    onValidityChange?.(canSubmit)
  }, [canSubmit, onValidityChange])

  // 封裝 payload
  const buildPayload = (): AddMemberForm => ({
    account: account.trim(),
    accountUserName,
    password: password.trim(),
    role,
    assignCredit: Number(credit || 0),
    alias: alias.trim(),
    note: note.trim(),
  })

  // 提供給父層的 ref API
  useImperativeHandle(ref, () => ({
    isValid: () => canSubmit && !loading,
    getPayload: () => (canSubmit ? buildPayload() : null),
    triggerSubmit: () => {
      if (canSubmit && !loading) {
        onSubmit(buildPayload())
      }
    },
  }))

  return (
    <>
      <div className="space-y-5">
        {/* Login Account */}
        <div>
          <FieldLabel required>
            <Trans i18nKey="team.member.loginAccount">Login Account</Trans>
          </FieldLabel>
          <div className="mt-2 flex items-center gap-3">
            <div className="flex-1 relative">
              <input
                value={account}
                onChange={(e) => sanitizeAccountInput(e.target.value)}
                placeholder={t('team.member.form.usernamePlaceholder')}
                className="w-full h-11 px-4 pr-40 rounded-lg border border-neutral-200 outline-none focus:border-primary t-sm"
              />
              {teamDomain ? (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 t-sb text-secondary select-none">
                  @{teamDomain}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="t-sb text-btn-primary"
              onClick={() => sanitizeAccountInput(randomUsername())}
            >
              <Trans i18nKey="team.member.random">Random</Trans>
            </button>
          </div>
          {!isUsernameValid && account ? (
            <div className="t-xs text-error mt-2">{t('team.member.form.usernameValidation')}</div>
          ) : null}
          {teamDomain ? (
            <div className="t-xs text-secondary mt-1">
              {t('team.member.form.finalLoginWillBe', {
                login: `${account || 'username'}@${teamDomain}`,
              })}
            </div>
          ) : null}
          {domainWarn ? <div className="t-xs text-warning mt-1">{domainWarn}</div> : null}
        </div>

        {/* Password */}
        <div>
          <FieldLabel required>
            <Trans i18nKey="team.member.loginPassword">Login Password</Trans>
          </FieldLabel>
          <div className="mt-2 flex items-center gap-3">
            <div className="flex-1">
              <TextInput
                value={password}
                onChange={setPassword}
                placeholder={t('team.member.form.passwordPlaceholder')}
              />
              {hasSpace ? (
                <div className="t-xs text-error mt-2" aria-live="polite">
                  {t('team.member.form.passwordNoSpaces')}
                </div>
              ) : tooShort ? (
                <div className="t-xs text-error mt-2" aria-live="polite">
                  {t('team.member.form.passwordMinLength', { min: PASS_MIN })}
                </div>
              ) : tooLong ? (
                <div className="t-xs text-error mt-2" aria-live="polite">
                  {t('team.member.form.passwordMaxLength', { max: PASS_MAX })}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="t-sb text-btn-primary"
              onClick={() => setPassword(randomPassword())}
            >
              <Trans i18nKey="team.member.random">Random</Trans>
            </button>
          </div>
        </div>

        {/* Role */}
        <div>
          <FieldLabel required>
            <Trans i18nKey="team.member.role">Role</Trans>
          </FieldLabel>
          <div className="mt-2">
            <Dropdown
              rootClassName="w-full"
              options={roleOptions}
              value={role}
              onChange={(v) => setRole(v as TeamRole)}
              className="h-11"
              placement="left"
            />
          </div>
        </div>

        {/* Credits */}
        <div>
          <FieldLabel required>
            <Trans i18nKey="team.member.assignCredit">Assign Credit</Trans>
          </FieldLabel>
          <div className="mt-2 relative">
            <div className="absolute -top-8 right-0 t-sb text-secondary">
              <Trans i18nKey="team.member.balance">Balance</Trans>{' '}
              <span className="text-btn-primary">{formatPrice(balance, false, '')}</span>{' '}
              {t('team.member.form.credits')}
            </div>
            <input
              value={credit}
              onChange={(e) => setCredit(sanitizeTo2dp(e.target.value))}
              className="w-full h-11 px-4 pr-20 rounded-lg border border-neutral-200 outline-none focus:border-primary t-sm"
              placeholder="0"
            />
            {!enoughBalance && (
              <div className="absolute t-sm text-error">
                {t('team.member.form.insufficientBalance')}
              </div>
            )}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 t-sb text-secondary">
              {t('team.member.form.creditUnit')}
            </div>
          </div>
        </div>

        {/* Alias */}
        <div>
          <FieldLabel>
            <Trans i18nKey="team.member.alias">Alias</Trans>
          </FieldLabel>
          <div className="mt-2">
            <TextInput
              value={alias}
              onChange={setAlias}
              placeholder={t('team.member.form.aliasPlaceholder')}
            />
          </div>
        </div>

        {/* Note */}
        <div>
          <FieldLabel>
            <Trans i18nKey="team.member.note">Note</Trans>
          </FieldLabel>
        </div>
        <div className="mt-2">
          <TextArea
            value={note}
            onChange={setNote}
            placeholder={t('team.member.form.notePlaceholder')}
          />
        </div>
      </div>
    </>
  )
})
